/**
 * Rax Schedule Editor
 *
 * iOS-style editor for creating/editing scheduled automations.
 * Features big time picker, day-of-week pills, and prominent controls.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi, type AutomationRule } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

@customElement("rax-schedule-editor")
export class RaxScheduleEditor extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .editor-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        animation: fadeIn 0.2s ease-out;
      }
      
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      
      .editor-panel {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        width: 100%;
        max-width: 400px;
        max-height: 90vh;
        overflow-y: auto;
        animation: slideUp 0.3s ease-out;
      }
      
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .editor-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-4);
        border-bottom: 1px solid var(--border-color);
      }
      
      .back-btn {
        background: transparent;
        border: none;
        color: var(--rax-yellow);
        font-size: var(--font-md);
        cursor: pointer;
        padding: var(--space-2);
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }
      
      .back-btn:hover {
        opacity: 0.8;
      }
      
      .editor-title {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .save-btn {
        background: var(--rax-yellow);
        border: none;
        border-radius: var(--radius-md);
        padding: var(--space-2) var(--space-4);
        font-size: var(--font-md);
        font-weight: 600;
        color: #000;
        cursor: pointer;
      }
      
      .save-btn:hover {
        background: var(--rax-yellow-hover);
      }
      
      .save-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .editor-body {
        padding: var(--space-5);
      }
      
      /* Time Picker - iOS style */
      .time-picker {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-3);
        padding: var(--space-6) 0;
        background: var(--bg-elevated);
        border-radius: var(--radius-lg);
        margin-bottom: var(--space-5);
      }
      
      .time-column {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--space-2);
      }
      
      .time-btn {
        background: transparent;
        border: none;
        color: var(--text-tertiary);
        font-size: 1.5rem;
        cursor: pointer;
        padding: var(--space-2);
        line-height: 1;
        transition: color var(--transition-fast);
      }
      
      .time-btn:hover {
        color: var(--text-primary);
      }
      
      .time-value {
        font-size: 4rem;
        font-weight: 300;
        color: var(--text-primary);
        font-variant-numeric: tabular-nums;
        line-height: 1;
        min-width: 100px;
        text-align: center;
      }
      
      .time-colon {
        font-size: 4rem;
        font-weight: 300;
        color: var(--text-tertiary);
        line-height: 1;
      }
      
      .period-toggle {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }
      
      .period-btn {
        background: var(--bg-subtle);
        border: none;
        border-radius: var(--radius-sm);
        padding: var(--space-2) var(--space-3);
        font-size: var(--font-md);
        font-weight: 600;
        color: var(--text-tertiary);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .period-btn.active {
        background: var(--rax-yellow);
        color: #000;
      }
      
      /* Day Pills */
      .section-label {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-secondary);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: var(--space-3);
      }
      
      .day-pills {
        display: flex;
        gap: var(--space-2);
        margin-bottom: var(--space-5);
      }
      
      .day-pill {
        flex: 1;
        background: var(--bg-subtle);
        border: 2px solid transparent;
        border-radius: var(--radius-md);
        padding: var(--space-3) var(--space-2);
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-tertiary);
        cursor: pointer;
        text-align: center;
        transition: all var(--transition-fast);
      }
      
      .day-pill:hover {
        border-color: var(--border-color);
      }
      
      .day-pill.active {
        background: var(--rax-yellow);
        color: #000;
        border-color: var(--rax-yellow);
      }
      
      /* Quick presets */
      .presets {
        display: flex;
        gap: var(--space-2);
        margin-bottom: var(--space-5);
      }
      
      .preset-btn {
        background: var(--bg-subtle);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-2) var(--space-3);
        font-size: var(--font-xs);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .preset-btn:hover {
        background: var(--bg-hover);
        border-color: var(--text-tertiary);
      }
      
      .preset-btn.active {
        background: var(--rax-yellow);
        border-color: var(--rax-yellow);
        color: #000;
      }
      
      /* Form fields */
      .form-group {
        margin-bottom: var(--space-4);
      }
      
      .form-label {
        display: block;
        font-size: var(--font-sm);
        font-weight: 500;
        color: var(--text-secondary);
        margin-bottom: var(--space-2);
      }
      
      .form-input {
        width: 100%;
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        font-size: var(--font-md);
        color: var(--text-primary);
      }
      
      .form-input:focus {
        outline: none;
        border-color: var(--rax-yellow);
      }
      
      .form-input::placeholder {
        color: var(--text-tertiary);
      }
      
      /* Next run preview */
      .next-run-preview {
        background: var(--bg-elevated);
        border-radius: var(--radius-md);
        padding: var(--space-4);
        margin-bottom: var(--space-5);
        text-align: center;
      }
      
      .next-run-label {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: var(--space-1);
      }
      
      .next-run-time {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--rax-yellow);
      }
      
      /* Delete */
      .delete-section {
        border-top: 1px solid var(--border-color);
        padding-top: var(--space-4);
        margin-top: var(--space-4);
      }
      
      .delete-btn {
        width: 100%;
        background: transparent;
        border: 1px solid var(--error);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        font-size: var(--font-md);
        font-weight: 500;
        color: var(--error);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .delete-btn:hover {
        background: rgba(239, 68, 68, 0.1);
      }
      
      @media (max-width: 480px) {
        .editor-panel {
          max-width: 100%;
          height: 100%;
          max-height: 100%;
          border-radius: 0;
        }
      
        .time-value {
          font-size: 3rem;
          min-width: 70px;
        }
      
        .time-colon {
          font-size: 3rem;
        }
      
        .day-pill {
          padding: var(--space-2) var(--space-1);
          font-size: var(--font-xs);
        }
      }
    `,
  ];

  @property({ type: Object })
  rule: AutomationRule | null = null;

  @property({ type: Boolean })
  open = false;

  @state()
  private hour = 9;

  @state()
  private minute = 0;

  @state()
  private isPM = false;

  @state()
  private days: number[] = [1, 2, 3, 4, 5]; // Weekdays

  @state()
  private name = "";

  @state()
  private action = "";

  @state()
  private saving = false;

  @state()
  private deleting = false;

  override updated(changed: Map<string, unknown>) {
    if (changed.has("rule") && this.rule) {
      this.initFromRule(this.rule);
    }
    if (changed.has("open") && this.open && !this.rule) {
      // Reset for new schedule
      this.hour = 9;
      this.minute = 0;
      this.isPM = false;
      this.days = [1, 2, 3, 4, 5];
      this.name = "";
      this.action = "";
    }
  }

  private initFromRule(rule: AutomationRule) {
    this.name = rule.name;
    this.action = rule.action;

    const config = rule.triggerConfig as Record<string, unknown>;
    const scheduleStr = config.schedule as string;

    if (scheduleStr) {
      const timeMatch = scheduleStr.match(/(\d{1,2}):(\d{2})/);
      if (timeMatch) {
        let h = parseInt(timeMatch[1], 10);
        this.minute = parseInt(timeMatch[2], 10);
        this.isPM = h >= 12;
        this.hour = h % 12 || 12;
      }

      // Parse days
      const lowerStr = scheduleStr.toLowerCase();
      if (lowerStr.includes("weekday")) {
        this.days = [1, 2, 3, 4, 5];
      } else if (lowerStr.includes("weekend")) {
        this.days = [0, 6];
      } else if (lowerStr.includes("every day") || lowerStr.includes("daily")) {
        this.days = [0, 1, 2, 3, 4, 5, 6];
      }
    }
  }

  private incrementHour() {
    this.hour = this.hour >= 12 ? 1 : this.hour + 1;
  }

  private decrementHour() {
    this.hour = this.hour <= 1 ? 12 : this.hour - 1;
  }

  private incrementMinute() {
    this.minute = (this.minute + 5) % 60;
  }

  private decrementMinute() {
    this.minute = this.minute < 5 ? 55 : this.minute - 5;
  }

  private toggleDay(day: number) {
    if (this.days.includes(day)) {
      this.days = this.days.filter((d) => d !== day);
    } else {
      this.days = [...this.days, day].sort();
    }
  }

  private setPreset(preset: "weekdays" | "weekends" | "everyday") {
    switch (preset) {
      case "weekdays":
        this.days = [1, 2, 3, 4, 5];
        break;
      case "weekends":
        this.days = [0, 6];
        break;
      case "everyday":
        this.days = [0, 1, 2, 3, 4, 5, 6];
        break;
    }
  }

  private get24Hour(): number {
    if (this.isPM) {
      return this.hour === 12 ? 12 : this.hour + 12;
    }
    return this.hour === 12 ? 0 : this.hour;
  }

  private getNextRunPreview(): string {
    const now = new Date();
    const today = now.getDay();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const scheduleMinutes = this.get24Hour() * 60 + this.minute;

    for (let offset = 0; offset <= 7; offset++) {
      const checkDay = (today + offset) % 7;
      if (this.days.includes(checkDay)) {
        if (offset === 0 && scheduleMinutes > currentMinutes) {
          return `Today at ${this.hour}:${this.minute.toString().padStart(2, "0")} ${this.isPM ? "PM" : "AM"}`;
        } else if (offset === 1 || (offset === 0 && scheduleMinutes <= currentMinutes)) {
          const nextDay = offset === 0 ? "Tomorrow" : DAY_LABELS[(today + 1) % 7];
          return `${nextDay} at ${this.hour}:${this.minute.toString().padStart(2, "0")} ${this.isPM ? "PM" : "AM"}`;
        } else {
          return `${DAY_LABELS[checkDay]} at ${this.hour}:${this.minute.toString().padStart(2, "0")} ${this.isPM ? "PM" : "AM"}`;
        }
      }
    }
    return "Select at least one day";
  }

  private buildScheduleString(): string {
    const h = this.get24Hour();
    const m = this.minute.toString().padStart(2, "0");
    let daysStr = "";

    if (this.days.length === 7) {
      daysStr = "Every day";
    } else if (this.days.length === 5 && !this.days.includes(0) && !this.days.includes(6)) {
      daysStr = "Weekdays";
    } else if (this.days.length === 2 && this.days.includes(0) && this.days.includes(6)) {
      daysStr = "Weekends";
    } else {
      daysStr = this.days.map((d) => DAY_LABELS[d]).join(", ");
    }

    return `${h}:${m} ${daysStr}`;
  }

  private async handleSave() {
    if (!this.name.trim() || !this.action.trim() || this.days.length === 0) {
      return;
    }

    this.saving = true;

    try {
      const schedule = this.buildScheduleString();

      if (this.rule) {
        // Update existing
        // Note: API would need an update endpoint
        console.log("[ScheduleEditor] Would update:", this.rule.id, {
          schedule,
          name: this.name,
          action: this.action,
        });
      } else {
        // Create new
        const response = await raxApi.createAutomationRule({
          name: this.name,
          triggerType: "schedule",
          schedule,
          action: this.action,
          sourceMessage: `Created via schedule editor: ${schedule}`,
        });

        if (response.success) {
          this.dispatchEvent(
            new CustomEvent("schedule-saved", {
              detail: { rule: response.data },
              bubbles: true,
              composed: true,
            }),
          );
          this.handleClose();
        }
      }
    } catch (err) {
      console.error("[ScheduleEditor] Error saving:", err);
    } finally {
      this.saving = false;
    }
  }

  private async handleDelete() {
    if (!this.rule) return;
    if (!confirm("Delete this schedule? This cannot be undone.")) return;

    this.deleting = true;

    try {
      const response = await raxApi.deleteAutomationRule(this.rule.id);
      if (response.success) {
        this.dispatchEvent(
          new CustomEvent("schedule-deleted", {
            detail: { id: this.rule.id },
            bubbles: true,
            composed: true,
          }),
        );
        this.handleClose();
      }
    } catch (err) {
      console.error("[ScheduleEditor] Error deleting:", err);
    } finally {
      this.deleting = false;
    }
  }

  private handleClose() {
    this.dispatchEvent(
      new CustomEvent("close", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    if (!this.open) return nothing;

    const isWeekdays = this.days.length === 5 && !this.days.includes(0) && !this.days.includes(6);
    const isWeekends = this.days.length === 2 && this.days.includes(0) && this.days.includes(6);
    const isEveryday = this.days.length === 7;
    const canSave = this.name.trim() && this.action.trim() && this.days.length > 0;

    return html`
      <div class="editor-overlay" @click=${(e: Event) => e.target === e.currentTarget && this.handleClose()}>
        <div class="editor-panel">
          <div class="editor-header">
            <button class="back-btn" @click=${this.handleClose}>&larr; Back</button>
            <span class="editor-title">${this.rule ? "Edit Schedule" : "New Schedule"}</span>
            <button class="save-btn" ?disabled=${!canSave || this.saving} @click=${this.handleSave}>
              ${this.saving ? "Saving..." : "Save"}
            </button>
          </div>

          <div class="editor-body">
            <!-- Time Picker -->
            <div class="time-picker">
              <div class="time-column">
                <button class="time-btn" @click=${this.incrementHour}>&and;</button>
                <span class="time-value">${this.hour}</span>
                <button class="time-btn" @click=${this.decrementHour}>&or;</button>
              </div>
              <span class="time-colon">:</span>
              <div class="time-column">
                <button class="time-btn" @click=${this.incrementMinute}>&and;</button>
                <span class="time-value">${this.minute.toString().padStart(2, "0")}</span>
                <button class="time-btn" @click=${this.decrementMinute}>&or;</button>
              </div>
              <div class="period-toggle">
                <button class="period-btn ${!this.isPM ? "active" : ""}" @click=${() => (this.isPM = false)}>AM</button>
                <button class="period-btn ${this.isPM ? "active" : ""}" @click=${() => (this.isPM = true)}>PM</button>
              </div>
            </div>

            <!-- Day Pills -->
            <div class="section-label">Repeat</div>
            <div class="day-pills">
              ${DAY_LABELS.map(
                (label, idx) => html`
                  <button class="day-pill ${this.days.includes(idx) ? "active" : ""}" @click=${() => this.toggleDay(idx)}>
                    ${label}
                  </button>
                `,
              )}
            </div>

            <!-- Presets -->
            <div class="presets">
              <button class="preset-btn ${isWeekdays ? "active" : ""}" @click=${() => this.setPreset("weekdays")}>
                Weekdays
              </button>
              <button class="preset-btn ${isWeekends ? "active" : ""}" @click=${() => this.setPreset("weekends")}>
                Weekends
              </button>
              <button class="preset-btn ${isEveryday ? "active" : ""}" @click=${() => this.setPreset("everyday")}>
                Every day
              </button>
            </div>

            <!-- Next run preview -->
            <div class="next-run-preview">
              <div class="next-run-label">Next run</div>
              <div class="next-run-time">${this.getNextRunPreview()}</div>
            </div>

            <!-- Name -->
            <div class="form-group">
              <label class="form-label">Name</label>
              <input
                class="form-input"
                type="text"
                placeholder="e.g., Daily PR Review"
                .value=${this.name}
                @input=${(e: Event) => (this.name = (e.target as HTMLInputElement).value)}
              />
            </div>

            <!-- Action -->
            <div class="form-group">
              <label class="form-label">What should happen?</label>
              <input
                class="form-input"
                type="text"
                placeholder="e.g., Check my GitHub for open PRs and summarize them"
                .value=${this.action}
                @input=${(e: Event) => (this.action = (e.target as HTMLInputElement).value)}
              />
            </div>

            <!-- Delete -->
            ${
              this.rule
                ? html`
                  <div class="delete-section">
                    <button class="delete-btn" ?disabled=${this.deleting} @click=${this.handleDelete}>
                      ${this.deleting ? "Deleting..." : "Delete Schedule"}
                    </button>
                  </div>
                `
                : nothing
            }
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-schedule-editor": RaxScheduleEditor;
  }
}
